// src/components/auth/AuthGuard.tsx
'use client';

import { useEffect } from 'react';
import { useAuth } from '@/hooks/auth/useAuth';
import { usePathname, useRouter } from 'next/navigation';
import { ROUTES } from '@/config/routes';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
}

export default function AuthGuard({ 
  children, 
  requireAuth = false,
  requireAdmin = false 
}: AuthGuardProps) {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    // Skip if still loading
    if (isLoading) return;

    // Public route, no auth required
    if (!requireAuth) return;

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      router.replace(`${ROUTES.SIGNIN.path}?redirect=${encodeURIComponent(pathname)}`);
      return;
    }

    // Check admin requirement — 'admin' is the super-user, there's no separate super-admin tier
    if (requireAdmin && !isAdmin) {
      router.replace(ROUTES.DASHBOARD.path);
      return;
    }
  }, [isAuthenticated, isAdmin, isLoading, requireAuth, requireAdmin, pathname, router]);

  // Show nothing while checking or redirecting
  if (isLoading || (requireAuth && !isAuthenticated) || (requireAdmin && isAuthenticated && !isAdmin)) {
    return null;
  }

  return <>{children}</>;
}